import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { auditoriaAPI } from '../services/api';
import { useZonaHoraria } from '../context/ZonaHorariaContext';

const FILTROS_INICIALES = {
  estado: '',
  telefono: '',
  usuario_id: '',
  desde: '',
  hasta: '',
  limit: 100,
  offset: 0,
};

const ESTADO_LABEL = { enviado: 'Enviado', rechazado: 'Rechazado' };
const CANAL_LABEL = { embebido: 'Integrado en el servidor', servicio: 'Microservicio WhatsApp' };

function formatNumero(n) {
  return n ? `+${n}` : '-';
}

const AuditoriaWhatsapp = () => {
  const { formatFechaHora } = useZonaHoraria();
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [registros, setRegistros] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState({ enviados: 0, rechazados: 0 });
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: filtros.limit, offset: filtros.offset };
      ['estado', 'telefono', 'usuario_id', 'desde', 'hasta'].forEach((k) => {
        if (filtros[k]) params[k] = filtros[k];
      });
      const res = await auditoriaAPI.getMensajesWhatsapp(params);
      if (res.success) {
        setRegistros(res.data || []);
        setTotal(res.total || 0);
        setResumen(res.resumen || { enviados: 0, rechazados: 0 });
      }
    } catch (error) {
      toast.error(error.message || 'Error al cargar mensajes de WhatsApp');
      setRegistros([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const handleFiltro = (campo, valor) => {
    setExpandido(null);
    setFiltros((prev) => ({ ...prev, [campo]: valor, offset: 0 }));
  };

  const paginas = Math.ceil(total / (filtros.limit || 1));
  const paginaActual = Math.floor(filtros.offset / filtros.limit) + 1;

  return (
    <div className="auditoria-actividad-panel">
      <div className="auditoria-filtros">
        <div className="filtro-group">
          <label htmlFor="wa-estado">Estado</label>
          <select id="wa-estado" value={filtros.estado} onChange={(e) => handleFiltro('estado', e.target.value)}>
            <option value="">Todos</option>
            <option value="enviado">Enviados</option>
            <option value="rechazado">Rechazados</option>
          </select>
        </div>
        <div className="filtro-group">
          <label htmlFor="wa-telefono">Teléfono (origen o destino)</label>
          <input
            id="wa-telefono"
            type="text"
            inputMode="numeric"
            placeholder="ej. 2954"
            value={filtros.telefono}
            onChange={(e) => handleFiltro('telefono', e.target.value)}
          />
        </div>
        <div className="filtro-group">
          <label htmlFor="wa-desde">Desde</label>
          <input
            id="wa-desde"
            type="datetime-local"
            value={filtros.desde}
            onChange={(e) => handleFiltro('desde', e.target.value)}
          />
        </div>
        <div className="filtro-group">
          <label htmlFor="wa-hasta">Hasta</label>
          <input
            id="wa-hasta"
            type="datetime-local"
            value={filtros.hasta}
            onChange={(e) => handleFiltro('hasta', e.target.value)}
          />
        </div>
        <div className="filtro-group">
          <label htmlFor="wa-usuario">Usuario ID</label>
          <input
            id="wa-usuario"
            type="number"
            placeholder="ID"
            value={filtros.usuario_id}
            onChange={(e) => handleFiltro('usuario_id', e.target.value)}
          />
        </div>
        <button type="button" className="btn-secondary" onClick={cargar}>
          Actualizar
        </button>
      </div>

      <p className="wa-log-resumen" aria-live="polite">
        {total} {total === 1 ? 'mensaje' : 'mensajes'}
        <span className="wa-estado-badge wa-estado--enviado">{resumen.enviados} enviados</span>
        <span className="wa-estado-badge wa-estado--rechazado">{resumen.rechazados} rechazados</span>
      </p>

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <div className="listado-resultado-panel">
          <div className="listado-tabla-area clientes-listado-tabla-area">
            <div className="table-container">
              <table className="data-table wa-log-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Desde</th>
                    <th>Para</th>
                    <th>Tipo</th>
                    <th>Estado</th>
                    <th>Mensaje</th>
                    <th aria-label="Detalle" />
                  </tr>
                </thead>
                <tbody>
                  {registros.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-data">No hay mensajes registrados</td>
                    </tr>
                  ) : (
                    registros.map((r) => (
                      <React.Fragment key={r.id}>
                        <tr>
                          <td>{formatFechaHora(r.created_at)}</td>
                          <td>{r.usuario_nombre ? `${r.usuario_nombre} (${r.usuario_username})` : '-'}</td>
                          <td>{formatNumero(r.numero_origen)}</td>
                          <td>
                            {r.destinatario && <div className="wa-log-destinatario">{r.destinatario}</div>}
                            <div>{formatNumero(r.telefono_destino)}</div>
                          </td>
                          <td>{r.tipo}</td>
                          <td>
                            <span
                              className={`wa-estado-badge wa-estado--${r.estado}`}
                              title={r.estado === 'rechazado' ? r.error_detalle || '' : undefined}
                            >
                              {ESTADO_LABEL[r.estado] || r.estado}
                            </span>
                          </td>
                          <td className="detalle-cell">{r.mensaje || '-'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn-secondary wa-log-ver"
                              aria-expanded={expandido === r.id}
                              onClick={() => setExpandido((prev) => (prev === r.id ? null : r.id))}
                            >
                              {expandido === r.id ? 'Ocultar' : 'Ver'}
                            </button>
                          </td>
                        </tr>
                        {expandido === r.id && (
                          <tr className="wa-log-detalle-fila">
                            <td colSpan="8">
                              <dl className="wa-log-detalle">
                                {r.estado === 'rechazado' && (
                                  <>
                                    <dt>Motivo del rechazo</dt>
                                    <dd className="wa-log-error">
                                      {r.error_detalle || 'Sin detalle'}
                                      {r.error_codigo ? ` (${r.error_codigo})` : ''}
                                    </dd>
                                  </>
                                )}
                                <dt>Plantilla</dt>
                                <dd>{r.template_nombre || '-'}</dd>
                                <dt>Adjunto</dt>
                                <dd>{r.archivo_nombre || '-'}</dd>
                                <dt>Canal</dt>
                                <dd>{CANAL_LABEL[r.canal] || r.canal}</dd>
                                <dt>Lote</dt>
                                <dd>{r.lote_id || '-'}</dd>
                                <dt>Mensaje completo</dt>
                                <dd className="wa-log-mensaje">{r.mensaje || '-'}</dd>
                              </dl>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {total > filtros.limit && (
            <div className="paginacion">
              <button
                type="button"
                className="btn-secondary"
                disabled={filtros.offset === 0}
                onClick={() => setFiltros((prev) => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
              >
                Anterior
              </button>
              <span>Página {paginaActual} de {paginas} ({total} mensajes)</span>
              <button
                type="button"
                className="btn-secondary"
                disabled={filtros.offset + filtros.limit >= total}
                onClick={() => setFiltros((prev) => ({ ...prev, offset: prev.offset + prev.limit }))}
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditoriaWhatsapp;
